import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Button from '../components/Button'
import Icon from '../components/Icon'
import styles from './Login.module.css'
import { resetPw, verifyResetCode } from '../api/security'

const SPECIAL_CHAR_RE = /[!@#$%^&*]/

export default function ResetPw() {
  const { resetKey }                        = useParams()
  const navigate                            = useNavigate()

  // Direct navigation here (bookmarked link, guessed URL, no prior "forgot
  // password" flow) must not render the set-password form for an unvalidated
  // code — verify resetKey server-side before showing anything but a loading
  // state. 'checking' | 'valid' | 'invalid'.
  const [codeStatus,      setCodeStatus]    = useState('checking')

  const [password,        setPassword]      = useState('')
  const [confirm,         setConfirm]       = useState('')
  const [showPassword,    setShowPassword]  = useState(false)
  const [showConfirm,     setShowConfirm]   = useState(false)
  const [submitting,      setSubmitting]    = useState(false)
  const [error,           setError]         = useState('')
  const [success,         setSuccess]       = useState(false)

  useEffect(() => {
    let cancelled = false
    verifyResetCode(resetKey)
      .then(() => { if (!cancelled) setCodeStatus('valid') })
      .catch(() => { if (!cancelled) setCodeStatus('invalid') })
    return () => { cancelled = true }
  }, [resetKey])

  const hasLength  = password.length >= 8
  const hasSpecial = SPECIAL_CHAR_RE.test(password)
  const matches    = password.length > 0 && password === confirm
  const isValid    = hasLength && hasSpecial && matches

  async function handleSubmit(e) {
    e.preventDefault()
    if (!isValid) return
    setError('')
    setSubmitting(true)
    try {
      await resetPw(resetKey, password)
      setSuccess(true)
      setTimeout(() => navigate('/login'), 2000)
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const logo = (
    <div className={styles.logoRow}>
      <img src="/GaussIQlogo.png" alt="GaussIQ Logo" className={styles.logoImage} />
      <div className={styles.logoText}>
        <span className={styles.logoName}>GaussIQ</span>
        <span className={styles.logoTagline}>
          Know your customer<br />Grow your future.
        </span>
      </div>
    </div>
  )

  if (codeStatus === 'checking') {
    return (
      <div className={styles.pageBackground}>
        <div className={styles.card}>
          {logo}
          <p className={styles.pageHeading}>Checking your reset link…</p>
        </div>
      </div>
    )
  }

  if (codeStatus === 'invalid') {
    return (
      <div className={styles.pageBackground}>
        <div className={styles.card}>
          {logo}
          <p className={styles.pageHeading}>This reset link is invalid or expired</p>
          <p className={styles.errorMessage}>
            Request a new password reset code to continue.
          </p>
          <div className={styles.buttonRow}>
            <Button type="button" variant="primary" fullWidth onClick={() => navigate('/resetcode')}>
              Request a new code
            </Button>
          </div>
          <div className={styles.links}>
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => navigate('/login')}
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.pageBackground}>
      <div className={styles.card}>

        {logo}

        <form onSubmit={handleSubmit} className={styles.form}>

          <p className={styles.pageHeading}>Set a new password</p>

          {/* Info banner */}
          <div className={styles.infoMessage}>
            <Icon name="info" width="16" height="16" style={{ flexShrink: 0, marginTop: '1px' }} />
            Minimum of 8 characters with one special character (!@#$%^&amp;*).
          </div>

          {error   && <p className={styles.errorMessage}>{error}</p>}
          {success && <p className={styles.successMessage}>Password updated! Redirecting to login…</p>}

          {/* New Password */}
          <div className={styles.fieldGroup}>
            <label htmlFor="newPassword" className={styles.label}>New Password</label>
            <div className={styles.passwordWrapper}>
              <input
                id="newPassword"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className={styles.input}
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                className={styles.eyeButton}
                onClick={() => setShowPassword(prev => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <Icon name={showPassword ? 'eye-off' : 'eye'} width="18" height="18" />
              </button>
            </div>

            {/* Inline validation hints */}
            <ul className={styles.hintList}>
              <li className={hasLength ? styles.hintMet : styles.hint}>At least 8 characters</li>
              <li className={hasSpecial ? styles.hintMet : styles.hint}>One special character (!@#$%^&amp;*)</li>
            </ul>
          </div>

          {/* Confirm Password */}
          <div className={styles.fieldGroup}>
            <label htmlFor="confirmPassword" className={styles.label}>Confirm Password</label>
            <div className={styles.passwordWrapper}>
              <input
                id="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                placeholder="Re-enter new password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                className={styles.input}
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                className={styles.eyeButton}
                onClick={() => setShowConfirm(prev => !prev)}
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
              >
                <Icon name={showConfirm ? 'eye-off' : 'eye'} width="18" height="18" />
              </button>
            </div>
          </div>

          <div className={styles.buttonRow}>
            <Button type="submit" variant="primary" fullWidth disabled={!isValid || submitting}>
              {submitting ? 'Saving…' : 'Set New Password'}
            </Button>
          </div>

          <div className={styles.links}>
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => navigate('/login')}
            >
              Back to login
            </button>
          </div>

        </form>
      </div>
    </div>
  )
}
